<template>
  <el-dialog
    v-model="dialogVisible"
    :title="`${isEdit ? '编辑' : '新增'}商户`"
    :before-close="handleBeforeClose"
    :width="isProfileMode ? '700px' : '1400px'"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <el-steps v-if="!isProfileMode" :active="state.step - 1" simple class="user-steps">
      <el-step title="基本信息" />
      <el-step title="商户配置" />
    </el-steps>

    <div v-if="!isProfileMode && state.step === 2" class="basic-summary">
      <div class="summary-item">
        <span class="label">所属组别</span>
        <span class="value">{{ currentRoleLabel || "-" }}</span>
      </div>
      <div class="summary-item">
        <span class="label">商户名</span>
        <span class="value">{{ basicForm.userName || "-" }}</span>
      </div>
      <div class="summary-item" v-if="canViewRemark">
        <span class="label">备注</span>
        <span class="value">{{ basicForm.remark || "-" }}</span>
      </div>
    </div>

    <div v-loading="state.initializing">
    <el-form
      v-show="isProfileMode || state.step === 1"
      ref="basicFormRef"
      :model="basicForm"
      label-position="top"
      class="basic-form"
    >
      <el-form-item
        v-if="!isProfileMode"
        label="所属组别"
        prop="roleId"
        :rules="[{ required: true, message: '请选择所属组别', trigger: ['change'] }]"
      >
        <el-select
          v-model="basicForm.roleId"
          :disabled="isEdit || isCommonAdminCreateFlow"
          placeholder="请选择所属组别"
          no-data-text="暂无数据"
          style="width: 100%"
        >
          <el-option
            v-for="item in data.roleIdList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item
        label="商户名"
        prop="userName"
        :rules="[{ required: true, trigger: ['blur'], validator: checkuserName }]"
      >
        <el-input
          v-model="basicForm.userName"
          :disabled="isEdit"
          placeholder="请输入商户名"
          style="width: 100%"
        />
      </el-form-item>

      <el-form-item
        v-if="showPasswordField"
        label="密码"
        prop="password"
        :rules="passwordRules"
      >
        <el-input
          v-model="basicForm.password"
          :placeholder="isEdit ? '不修改密码请留空' : '请输入密码'"
          type="password"
          :maxlength="12"
          show-password
          show-word-limit
          style="width: 100%"
        >
          <template #append>
            <el-button @click="generatePassword">随机生成</el-button>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item v-if="isEdit && !isProfileMode" label="appKey">
        <div class="key-form">{{ basicForm.appKey || '-' }}</div>
      </el-form-item>

      <el-form-item v-if="isEdit && !isProfileMode" label="secretKey">
        <div class="key-form">{{ basicForm.secretKey || '-' }}</div>
      </el-form-item>

      <el-form-item v-if="canViewRemark" label="备注" prop="remark">
        <el-input
          v-model="basicForm.remark"
          placeholder="请输入备注"
          :maxlength="200"
          show-word-limit
          style="width: 100%"
        />
      </el-form-item>

      <el-form-item v-if="canViewAllConfig" label="停结算" prop="stopSettlement">
        <el-switch
          active-text="停结算"
          inactive-text="未停"
          :active-value="1"
          :inactive-value="0"
          v-model="basicForm.stopSettlement"
          :disabled="!canToggleStopSettlement"
        />
      </el-form-item>

    </el-form>

    <el-form
      v-if="!isProfileMode"
      v-show="state.step === 2"
      ref="configRuleForm"
      scroll-to-error
      class="rule-form"
      :model="configForm"
      label-position="right"
      label-width="170px"
    >
      <div class="flex-box flex-box-ban" v-if="canViewAllConfig">
        <el-form-item label="收款禁用" prop="banPayment">
          <el-switch
            style="width: 186px"
            active-text="禁用"
            inactive-text="启用"
            :active-value="1"
            :inactive-value="0"
            v-model="configForm.banPayment"
            :disabled="!canEditAllConfig"
          />
        </el-form-item>
        <el-form-item label="每日限额" prop="limitedDaily">
          <el-input :disabled="!canEditAllConfig" v-model="configForm.limitedDaily" placeholder="每日限额" style="width: 186px" />
        </el-form-item>
      </div>

      <p class="sub-title" v-if="canViewFeeConfig">费率</p>
      <div v-if="canViewFeeConfig">
        <template v-if="forceReadonlyConfig">
          <div class="fee-card">
            <div class="fee-grid">
              <div class="fee-item">
                <div class="fee-label">支付费率</div>
                <div class="fee-value">{{ configForm.paymentFeeRate || "-" }}</div>
              </div>
              <div class="fee-item">
                <div class="fee-label">保证金比例</div>
                <div class="fee-value">{{ configForm.depositRate || "-" }}</div>
              </div>
              <div class="fee-item">
                <div class="fee-label">循环保证金天数</div>
                <div class="fee-value">{{ configForm.revolvingMarginDays ?? "-" }}</div>
              </div>
              <div class="fee-item">
                <div class="fee-label">成功交易服务费</div>
                <div class="fee-value">{{ configForm.singleTradeFee ?? "-" }}</div>
              </div>
              <div class="fee-item">
                <div class="fee-label">失败交易服务费</div>
                <div class="fee-value">{{ configForm.failSingleTradeFee ?? "-" }}</div>
              </div>
              <div class="fee-item">
                <div class="fee-label">拒付争议费(笔)</div>
                <div class="fee-value">{{ configForm.chargebackFee ?? "-" }}</div>
              </div>
              <div class="fee-item">
                <div class="fee-label">退款费用</div>
                <div class="fee-value">{{ configForm.returnFeePer ?? "-" }}</div>
              </div>
              <div class="fee-item">
                <div class="fee-label">提现手续费</div>
                <div class="fee-value">{{ configForm.withdrawFeeRate || "-" }}</div>
              </div>
              <div class="fee-item">
                <div class="fee-label">退款时承担费率</div>
                <div class="fee-value">{{ configForm.refundPaymentFeeRate || "-" }}</div>
              </div>
              <div class="fee-item">
                <div class="fee-label">退款时保证金承担费率</div>
                <div class="fee-value">{{ configForm.refundDepositRate || "-" }}</div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex-box flex-box-fee">
            <el-form-item
              label="支付费率"
              prop="paymentFeeRate"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input
                v-if="canEditAllConfig"
                v-model="configForm.paymentFeeRate"
                placeholder="请输入(小数, 0.02表示2%)"
                style="width: 186px"
                @input="value => handleDecimalInput(value, 'paymentFeeRate')"
              />
              <span v-else>{{ configForm.paymentFeeRate }}</span>
            </el-form-item>
            <el-form-item
              label="保证金比例"
              prop="depositRate"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input
                v-if="canEditAllConfig"
                v-model="configForm.depositRate"
                placeholder="请输入保证金比例"
                style="width: 186px"
              />
              <span v-else>{{ configForm.depositRate }}</span>
            </el-form-item>
            <el-form-item
              label="循环保证金天数"
              prop="revolvingMarginDays"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input-number
                v-if="canEditAllConfig"
                v-model="configForm.revolvingMarginDays"
                :min="0"
                :max="360"
                :step="1"
                placeholder="请输入循环保证金天数"
                style="width: 186px"
              />
              <span v-else>{{ configForm.revolvingMarginDays }}</span>
            </el-form-item>
          </div>

          <div class="flex-box flex-box-fee">
            <el-form-item
              label="成功交易服务费"
              prop="singleTradeFee"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input-number
                v-if="canEditAllConfig"
                v-model="configForm.singleTradeFee"
                :min="0"
                :max="100"
                :step="1"
                placeholder="请输入成功交易服务费"
                style="width: 186px"
              />
              <span v-else>{{ configForm.singleTradeFee }}</span>
            </el-form-item>
            <el-form-item
              label="失败交易服务费"
              prop="failSingleTradeFee"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input-number
                v-if="canEditAllConfig"
                v-model="configForm.failSingleTradeFee"
                :min="0"
                :max="100"
                :step="1"
                placeholder="请输入失败交易服务费"
                style="width: 186px"
              />
              <span v-else>{{ configForm.failSingleTradeFee }}</span>
            </el-form-item>
            <el-form-item
              label="拒付争议费(笔)"
              prop="chargebackFee"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input-number
                v-if="canEditAllConfig"
                v-model="configForm.chargebackFee"
                :min="0"
                :step="1"
                placeholder="请输入拒付争议费"
                style="width: 186px"
              />
              <span v-else>{{ configForm.chargebackFee }}</span>
            </el-form-item>
          </div>

          <div class="flex-box flex-box-fee">
            <el-form-item
              label="退款费用"
              prop="returnFeePer"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input-number
                v-if="canEditAllConfig"
                v-model="configForm.returnFeePer"
                :min="0"
                :max="100"
                :step="1"
                placeholder="请输入退款费用"
                style="width: 186px"
              />
              <span v-else>{{ configForm.returnFeePer }}</span>
            </el-form-item>
            <el-form-item
              label="提现手续费"
              prop="withdrawFeeRate"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input
                v-if="canEditAllConfig"
                v-model="configForm.withdrawFeeRate"
                placeholder="请输入(小数, 0.02表示2%)"
                style="width: 186px"
              />
              <span v-else>{{ configForm.withdrawFeeRate }}</span>
            </el-form-item>
            <el-form-item
              v-if="canEditAllConfig"
              label="退款时承担费率"
              prop="refundPaymentFeeRate"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input
                v-model="configForm.refundPaymentFeeRate"
                placeholder="请输入(小数, 0.02表示2%)"
                style="width: 186px"
              />
            </el-form-item>
          </div>

          <div class="flex-box flex-box-fee">
            <el-form-item
              v-if="canEditAllConfig"
              label="退款时保证金承担费率"
              prop="refundDepositRate"
              :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
            >
              <el-input
                v-model="configForm.refundDepositRate"
                placeholder="请输入(小数, 0.02表示2%)"
                style="width: 186px"
              />
            </el-form-item>
          </div>
        </template>
      </div>

      <p class="sub-title" v-if="canViewAllConfig">结算</p>
      <div class="flex-box flex-box-settle" v-if="canViewAllConfig">
        <el-form-item
          label="结算日(T)"
          prop="settleDays"
          :rules="[{ required: true, message: '请输入', trigger: ['blur', 'change'] }]"
        >
          <el-input-number
            v-if="canEditAllConfig"
            v-model="configForm.settleDays"
            :min="0"
            :max="100"
            :step="1"
            placeholder="请输入结算日"
            style="width: 186px"
          />
          <span v-else>{{ configForm.settleDays }}</span>
        </el-form-item>
        <el-form-item
          label="结算币种"
          prop="settleCurrency"
          :rules="[{ required: true, message: '请选择结算币种', trigger: ['change'] }]"
        >
          <el-select :disabled="!canEditAllConfig" v-model="configForm.settleCurrency" placeholder="请选择结算币种" style="width: 186px">
            <el-option v-for="item in data.settleCurrencyList" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item
          label="结算系数"
          prop="settleFactor"
          :rules="[{ required: true, message: '请输入结算系数', trigger: ['blur'] }]"
        >
          <el-input :disabled="!canEditAllConfig" v-model="configForm.settleFactor" placeholder="请输入结算系数" style="width: 186px" />
        </el-form-item>
      </div>

      <div class="flex-box flex-box-settle" v-if="canViewAllConfig">
        <el-form-item
          label="支付系数"
          prop="paymentFactor"
          :rules="[
            { required: true, message: '请输入支付系数', trigger: ['blur'] },
            { validator: checkPaymentFactor, trigger: ['blur', 'change'] },
          ]"
        >
          <el-input
            :disabled="!canEditAllConfig"
            v-model="configForm.paymentFactor"
            placeholder="请输入1到1.1之间的数字"
            style="width: 186px"
          />
        </el-form-item>
        <el-form-item
          label="每周结算日"
          prop="settleWeek"
          class="settle-week-form-item"
          :rules="[{ required: true, message: '请选择每周结算日', trigger: 'change' }]"
        >
          <el-select :disabled="!canEditAllConfig" v-model="configForm.settleWeek" multiple placeholder="请选择每周结算日" style="width: 382px" class="settle-week-select">
            <el-option v-for="item in weekOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
      </div>

      <p class="sub-title" v-if="canViewAllConfig">通道配置</p>
      <tm-table
        v-if="canViewAllConfig"
        class="table-ref"
        :data="configForm.paymentSelectRules"
        :columns="data.columns"
        :hasSerialNumber="true"
      >
        <template #countryCodes="{ item, rowInfo }">
          <el-form-item
            v-if="item.isEdit"
            :prop="'paymentSelectRules.' + rowInfo.index + '.countryCodes'"
            label=" "
            label-width="2px"
            :rules="rules.countryCodes"
          >
            <el-tree-select
              v-model="item.countryCodes"
              :data="data.countryTree"
              :props="{ label: 'label', children: 'children', value: 'value' }"
              show-checkbox
              multiple
              check-strictly
              filterable
              clearable
              placeholder="全部"
              style="width: 180px;"
            />
          </el-form-item>
          <span v-else>{{ item.countryValue || '全部' }}</span>
        </template>
        <template #currencies="{ item, rowInfo }">
          <el-form-item
            v-if="item.isEdit"
            :prop="'paymentSelectRules.' + rowInfo.index + '.currencies'"
            label=" "
            label-width="2px"
          >
            <el-select multiple v-model="item.currencies" placeholder="全部" clearable style="width: 120px;">
              <el-option v-for="v in data.settleCurrencyList" :key="v.value" :label="v.label" :value="v.value" />
            </el-select>
          </el-form-item>
          <span v-else>{{ item.currencies?.length ? item.currencies.join(',') : '全部' }}</span>
        </template>
        <template #wayType="{ item, rowInfo }">
          <el-form-item
            v-if="item.isEdit"
            :prop="'paymentSelectRules.' + rowInfo.index + '.wayType'"
            label=" "
            label-width="2px"
          >
            <el-select v-model="item.wayType" placeholder="CARD" style="width: 120px" @change="() => handleWayTypeChange(item)">
              <el-option v-for="v in data.wayTypeList" :key="v.value" :label="v.label" :value="v.value" />
            </el-select>
          </el-form-item>
          <span v-else>{{ item.wayType || 'CARD' }}</span>
        </template>
        <template #cardKinds="{ item, rowInfo }">
          <el-form-item
            v-if="item.isEdit"
            :prop="'paymentSelectRules.' + rowInfo.index + '.cardKinds'"
            label=" "
            label-width="2px"
            :rules="rules.cardKinds"
          >
            <el-select multiple v-model="item.cardKinds" placeholder="全部" no-data-text="暂无数据" style="width: 140px">
              <el-option v-for="v in cardKindsOptions(item.wayType)" :key="v.value" :label="v.label" :value="v.value" />
            </el-select>
          </el-form-item>
          <span v-else>{{ item.cardKindsValue || '全部' }}</span>
        </template>
        <template #minAmount="{ item, rowInfo }">
          <el-form-item
            v-if="item.isEdit"
            :prop="'paymentSelectRules.' + rowInfo.index + '.minAmount'"
            label=" "
            label-width="20px"
            :rules="rules.minAmount"
          >
            <el-input v-model="item.minAmount" placeholder="请输入" style="width: 140px" />
          </el-form-item>
          <span v-else>{{ item.minAmount }}</span>
        </template>
        <template #maxAmount="{ item, rowInfo }">
          <el-form-item
            v-if="item.isEdit"
            :prop="'paymentSelectRules.' + rowInfo.index + '.maxAmount'"
            label=" "
            label-width="20px"
            :rules="rules.maxAmount"
          >
            <el-input v-model="item.maxAmount" placeholder="请输入" style="width: 140px" />
          </el-form-item>
          <span v-else>{{ item.maxAmount }}</span>
        </template>
        <template #limitedDailyAmount="{ item, rowInfo }">
          <el-form-item
            v-if="item.isEdit"
            :prop="'paymentSelectRules.' + rowInfo.index + '.limitedDailyAmount'"
            label=" "
            label-width="20px"
            :rules="rules.limitedDailyAmount"
          >
            <el-input v-model="item.limitedDailyAmount" placeholder="请输入" style="width: 140px" />
          </el-form-item>
          <span v-else>{{ item.limitedDailyAmount }}</span>
        </template>
        <template #weight="{ item, rowInfo }">
          <el-form-item
            v-if="item.isEdit"
            :prop="'paymentSelectRules.' + rowInfo.index + '.weight'"
            label=" "
            label-width="20px"
            :rules="rules.weight"
          >
            <el-input v-model="item.weight" placeholder="请输入" style="width: 140px" />
          </el-form-item>
          <span v-else>{{ item.weight }}</span>
        </template>
        <template #paymentAccountId="{ item, rowInfo }">
          <el-form-item
            v-if="item.isEdit"
            :prop="'paymentSelectRules.' + rowInfo.index + '.paymentAccountId'"
            label=" "
            label-width="20px"
            :rules="rules.paymentAccountId"
          >
            <el-select v-model="item.paymentAccountId" placeholder="请选择" no-data-text="暂无数据" style="width: 140px">
              <el-option v-for="v in data.paymentAccountList" :key="v.value" :label="v.label" :value="v.value" />
            </el-select>
          </el-form-item>
          <span v-else>{{ item.paymentAccountName }}</span>
        </template>
        <template #operation="{ item, rowInfo }">
          <el-button v-if="!forceReadonlyConfig && !item.isEdit" type="primary" link :icon="EditPen" title="编辑" @click="item.isEdit = true" />
          <el-button v-if="!forceReadonlyConfig && item.isEdit" type="primary" link :icon="Check" title="保存" @click="saveTable(item, rowInfo.index)" />
          <el-button v-if="!forceReadonlyConfig" type="primary" link :icon="Delete" title="删除" @click="deleteConfigRule(rowInfo.index)" />
        </template>
      </tm-table>
      <el-button v-if="canEditAllConfig" type="primary" style="margin-top: 10px;" :icon="Plus" title="新增" @click="addItem" />

      <p class="sub-title" v-if="canViewAllConfig">其他配置</p>
      <div class="flex-box flex-box-other" v-if="canViewAllConfig">
        <el-form-item label="自动发送邮件" prop="autoMail">
          <el-switch :disabled="!canEditAllConfig" active-text="是" inactive-text="否" :active-value="1" :inactive-value="0" style="width: 100px;" v-model="configForm.autoMail" />
        </el-form-item>
        <el-form-item label="单客户下单次数" prop="autoMail">
          <el-select :disabled="!canEditAllConfig" v-model="configForm.singleCustomerLimitRule" placeholder="请选择" style="width: 80px">
            <el-option key="DAY" label="每日" value="DAY" />
            <el-option key="MONTH" label="每月" value="MONTH" />
          </el-select>
          <el-input :disabled="!canEditAllConfig" v-model="configForm.countLimit" style="width: 80px" /> &nbsp;次
        </el-form-item>
        <el-form-item label="A站需要审核" prop="auditSwitch">
          <el-switch :disabled="!canEditAllConfig" active-text="需要" inactive-text="不需要" :active-value="1" :inactive-value="0" v-model="configForm.auditSwitch" />
        </el-form-item>
      </div>
      <div class="flex-box flex-box-other" v-if="canViewAllConfig">
        <el-form-item label="支付页填写账单地址信息" prop="payPageBilling">
          <el-switch :disabled="!canEditAllConfig" active-text="是" inactive-text="否" :active-value="1" :inactive-value="0" style="width: 100px;" v-model="configForm.payPageBilling" />
        </el-form-item>
        <el-form-item label="支付页显示回退图标" prop="showBackIcon">
          <el-switch :disabled="!canEditAllConfig" active-text="是" inactive-text="否" :active-value="1" :inactive-value="0" style="width: 215px;" v-model="configForm.showBackIcon" />
        </el-form-item>
      </div>
      <div class="flex-box flex-box-other" v-if="canViewAllConfig">
        <el-form-item
          prop="failTimesLimit"
          label="单商户失败次数上限"
          :rules="[
            { required: false, message: '请输入', trigger: ['blur', 'change'] },
            { validator: (rule, value, callback) => checkValue(rule, value, callback, 'number', '请输入正整数'), trigger: ['blur', 'change'] }
          ]"
        >
          <el-input :disabled="!canEditAllConfig" v-model="configForm.failTimesLimit" placeholder="单商户失败次数上限" style="width: 100px;" />
        </el-form-item>

        <el-form-item prop="icon" label="支付页品牌图标">
          <file-upload
            v-if="canEditAllConfig && !configForm.icon"
            @getFiles="val => getFiles(val, 'icon')"
            accept=".png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.pdf"
            multiple
            :fileSize="200"
            maxNum="1"
          >
            <el-button type="primary">上传</el-button>
          </file-upload>
          <div v-else-if="configForm.icon" class="img-box">
            <el-image
              style="width: 100px; height: 100px"
              :src="configForm.icon"
              :zoom-rate="1.2"
              :max-scale="7"
              :min-scale="0.2"
              :preview-src-list="[configForm.icon]"
              show-progress
              :initial-index="0"
              fit="cover"
            />
            <el-icon v-if="canEditAllConfig" @click="configForm.icon = ''"><CircleCloseFilled /></el-icon>
          </div>
          <span v-else>-</span>
        </el-form-item>
      </div>
      <div class="flex-box flex-box-other" v-if="canViewAllConfig">
        <el-form-item
          v-if="canEditAllConfig && basicForm.roleId == 3"
          prop="syncChildConfig"
          label=""
        >
          <el-checkbox v-model="state.syncChildConfig" style="margin-left: -139px;" :value="true">同步下级商户配置</el-checkbox>
        </el-form-item>
      </div>
    </el-form>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleModalClose">取消</el-button>
        <el-button v-if="!isProfileMode && state.step === 2" @click="state.step = 1">上一步</el-button>
        <el-button :loading="state.submitting" :disabled="state.initializing" type="primary" @click="handlePrimaryAction">
          {{ primaryButtonText }}
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref, toRefs } from "vue"
import { dialogProps } from "@/mixins/dialog"
import { addUser, editUser } from "@/api/systemUser"
import { getRoleList } from "@/api/systemRole"
import { md5 } from "js-md5"
import Regulars from "@/utils/validate.js"
import TmTable from "@/components/common/Table.vue"
import FileUpload from "@/components/common/FileUpload.vue"
import { fetchCountryList } from "@/utils/countryContinent"
import { CircleCloseFilled, EditPen, Delete, Check, Plus } from "@element-plus/icons-vue"

const props = defineProps({
  ...dialogProps,
})

const $fetch = inject("$fetch")
const $tm_message_box = inject("$tm_message_box")
const $store = inject("$store")

const DEFAULT_SETTLE_WEEK = ["1", "2", "3", "4", "5"]

const basicFormRef = ref(null)
const configRuleForm = ref(null)
const initialBasicSnapshot = ref("")
const initialConfigSnapshot = ref("")

const buildDefaultConfig = () => ({
  banPayment: "",
  banPaymentDesc: "",
  paymentAccountId: "",
  paymentAccountName: "",
  cardKinds: "",
  payTimeout: "",
  oneOrderOnly: 0,
  oneOrderOnlyDesc: "",
  auditSwitch: 0,
  auditSwitchDesc: "",
  mailInterface: "",
  autoMail: 0,
  autoMailDesc: "",
  payPageBilling: 1,
  payPageBillingDesc: "",
  icon: "",
  showBackIcon: "",
  paymentFeeRate: 0.08,
  returnFeeRate: "",
  depositRate: 0.1,
  returnFeePer: 3,
  chargebackFee: 25,
  revolvingMarginDays: 180,
  settleDays: 5,
  singleTradeFee: 0.3,
  failSingleTradeFee: 0.3,
  withdrawFeeRate: 0.003,
  refundPaymentFeeRate: 0.08,
  refundDepositRate: 0.1,
  failTimesLimit: "",
  settleCurrency: "USD",
  settleFactor: 1,
  paymentFactor: 1,
  settleWeek: [...DEFAULT_SETTLE_WEEK],
  paymentSelectRules: [],
  singleCustomerLimitRule: "",
  countLimit: "",
  limitedDaily: 50000,
})

const state = reactive({
  dialogVisible: true,
  initializing: false,
  step: 1,
  submitting: false,
  syncChildConfig: false,
})

const basicForm = reactive({
  id: props.record?.id,
  userName: "",
  remark: "",
  password: "",
  roleId: "",
  appKey: "",
  secretKey: "",
  stopSettlement: 0,
})

const configForm = reactive(buildDefaultConfig())

const data = reactive({
  roleIdList: [],
  paymentAccountList: [],
  countries: [],
  countryTree: [],
  settleCurrencyList: [],
  cardTypeList: [],
  walletList: [],
  wayTypeList: [
    { label: "CARD", value: "CARD" },
    { label: "WALLET", value: "WALLET" },
  ],
  columns: [
    { name: "国家", value: "countryCodes", slot: true },
    { name: "币种", value: "currencies", slot: true },
    { name: "支付方式配置", value: "wayType", slot: true },
    { name: "支付种类", value: "cardKinds", slot: true },
    { name: "单笔最小", value: "minAmount", slot: true },
    { name: "单笔最大", value: "maxAmount", slot: true },
    { name: "当日最大", value: "limitedDailyAmount", slot: true },
    { name: "权重", value: "weight", slot: true },
    { name: "支付账号", value: "paymentAccountId", slot: true },
    { name: "操作", value: "operation", width: 100, slot: true, newline: true },
  ],
})

const rules = reactive({
  countryCodes: [{ required: false, message: "请选择", trigger: "blur" }],
  cardKinds: [{ required: false, message: "请选择", trigger: "blur" }],
  minAmount: [{ required: true, message: "请输入", trigger: "blur" }],
  maxAmount: [{ required: true, message: "请输入", trigger: "blur" }],
  limitedDailyAmount: [{ required: true, message: "请输入", trigger: "blur" }],
  weight: [{ required: true, message: "请输入", trigger: "blur" }],
  paymentAccountId: [{ required: true, message: "请选择", trigger: "blur" }],
})

const weekOptions = [
  { label: "周一", value: "1" },
  { label: "周二", value: "2" },
  { label: "周三", value: "3" },
  { label: "周四", value: "4" },
  { label: "周五", value: "5" },
  { label: "周六", value: "6" },
  { label: "周日", value: "7" },
]

const { dialogVisible } = toRefs(state)
const currentRoleId = computed(() => Number($store.state?.user?.roleId))
const isEdit = computed(() => !!basicForm.id)
const isProfileMode = computed(() => !!props.config?.flag)
const isCommonAdminCreateFlow = computed(() => !isProfileMode.value && !props.record?.id && currentRoleId.value === 3)
const canViewRemark = computed(() => [1, 2].includes(currentRoleId.value))
const forceReadonlyConfig = computed(() => !isProfileMode.value && !isEdit.value && state.step === 2 && currentRoleId.value === 3)
const canEditAllConfig = computed(() => [1, 2].includes(currentRoleId.value) && !forceReadonlyConfig.value)
const canViewAllConfig = computed(() => [1, 2].includes(currentRoleId.value))
const canToggleStopSettlement = computed(() => [1, 2].includes(currentRoleId.value))
const canViewFeeConfig = computed(() => [1, 2, 3].includes(currentRoleId.value))
const showPasswordField = computed(() => isProfileMode.value || !isEdit.value)
const primaryButtonText = computed(() => {
  if (isProfileMode.value) {
    return "提交"
  }
  return state.step === 1 ? "下一步" : "提交"
})
const currentRoleLabel = computed(() => data.roleIdList.find(item => Number(item.id) === Number(basicForm.roleId))?.name || "")
const passwordRules = computed(() => {
  if (!showPasswordField.value) {
    return []
  }
  return [
    {
      required: !isEdit.value,
      message: "请输入密码",
      trigger: ["change", "blur"],
    },
    {
      validator: validatePassword,
      trigger: ["change", "blur"],
    },
  ]
})

const PASSWORD_CHARSET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*()_+-=[]{}|;:,.?"

onMounted(async () => {
  state.initializing = true
  const requests = [getRoleIdList()]
  if (!isProfileMode.value) {
    requests.push(getPaymentAccount(), getCardTypeList(), getWalletList(), getCurrencyList(), getCountryList())
  }
  try {
    await Promise.all(requests)
    if (basicForm.id) {
      await loadUserDetail()
      if (!isProfileMode.value) {
        await loadUserConfigDetail()
      }
    }
    markFormSnapshot()
  } finally {
    state.initializing = false
  }
})

const getRoleIdList = async () => {
  const params = { current: 1, size: 1000 }
  const { records } = await getRoleList(params)
  const list = records || []
  if (isCommonAdminCreateFlow.value) {
    data.roleIdList = list.filter(v => Number(v.id) === 4)
    if (!basicForm.roleId) {
      basicForm.roleId = 4
    }
    return
  }
  data.roleIdList = list
}

const getPaymentAccount = async () => {
  const res = await $fetch.post("/base/select")({ type: "paymentAccount", ext: { scene: "available" } })
  data.paymentAccountList = res.data || []
}

const getCardTypeList = async () => {
  const res = await $fetch.post("/base/select")({ type: "CardTypeEnum" })
  data.cardTypeList = res.data || []
}

const getWalletList = async () => {
  const res = await $fetch.post("/base/select")({ type: "WalletEnum" })
  data.walletList = res.data || []
}

const getCurrencyList = async () => {
  const res = await $fetch.post("/base/select")({ type: "currency" })
  data.settleCurrencyList = res.data || []
}

const getCountryList = async () => {
  const result = await fetchCountryList($fetch, (level, msg, info) => {
    if (level === 'warn') {
      console.warn('[editUser] ' + msg, info)
      return
    }
    console.log(
      '[editUser] ' + msg,
      (info && info.countries) || 0,
      'tree leaf country nodes:',
      (info && info.leafCount) || 0,
      'hasCountryTree =>',
      !!(info && info.hasCountryTree)
    )
    if (info) {
      if (info.countryTreeSample && info.countryTreeSample.length) {
        console.log('[editUser] countryTree sample (first 2 nodes):', JSON.stringify(info.countryTreeSample, null, 2))
      }
      if (info.firstCountrySample) {
        console.log('[editUser] data.countries first item keys sample:', Object.keys(info.firstCountrySample), 'sample value:', JSON.stringify(info.firstCountrySample))
      }
    }
  })
  data.countries = result.countries || []
  data.countryTree = result.countryTree || []
}

const loadUserDetail = async () => {
  const res = await $fetch.get("/system/user/detail")({ id: basicForm.id })
  basicForm.roleId = res.data?.roleId
  basicForm.userName = res.data?.userName
  basicForm.remark = res.data?.remark
  basicForm.appKey = res.data?.appKey
  basicForm.secretKey = res.data?.secretKey
  if (res.data?.stopSettlement !== undefined && res.data?.stopSettlement !== null) {
    basicForm.stopSettlement = res.data.stopSettlement
  } else {
    basicForm.stopSettlement = 0
  }
}

const resetConfigForm = () => {
  const defaults = buildDefaultConfig()
  Object.keys(defaults).forEach(key => {
    configForm[key] = defaults[key]
  })
}

const loadUserConfigDetail = async () => {
  const res = await $fetch.get("/system/user/configDetail")({ id: basicForm.id })
  applyConfigDetailData(res.data)
}

const loadCurrentUserConfigAsTemplate = async () => {
  const currentUserId = await resolveCurrentUserId()
  if (!currentUserId) {
    return false
  }
  const res = await $fetch.get("/system/user/configDetail")({ id: currentUserId })
  if (res.code !== 0) {
    return false
  }
  applyConfigDetailData(res.data)
  return true
}

const normalizeRuleItem = (item) => {
  item.wayType = item.wayType || "CARD"
  item.currencies = Array.isArray(item.currencies) ? item.currencies : (item.currencies ? String(item.currencies).split(",") : [])
  if (!item.currencies?.length && item.currency) {
    item.currencies = [item.currency]
  }
  item.countryCodes = Array.isArray(item.countryCodes) ? item.countryCodes : (item.countryCodes ? String(item.countryCodes).split(",") : [])
  item.countryCodes = item.countryCodes.filter(v => !v.startsWith('continent_'))
  item.cardKinds = Array.isArray(item.cardKinds) ? item.cardKinds : (item.cardKinds ? String(item.cardKinds).split(",") : [])
  const cardKinds = cardKindsOptions(item.wayType).filter(v => item.cardKinds.includes(v.value))
  item.cardKindsValue = cardKinds.map(v => v.label).join(",")
  const countryCodes = data.countries.filter(v => item.countryCodes.includes(v.shortName))
  item.countryValue = countryCodes.map(v => v.name).join(",")
  const paymentAccount = data.paymentAccountList.find(v => String(item.paymentAccountId) === String(v.value))
  item.paymentAccountName = paymentAccount?.label
  if (item.paymentAccountId !== undefined && item.paymentAccountId !== null) {
    item.paymentAccountId = String(item.paymentAccountId)
  }
}

const resolveCurrentUserId = async () => {
  const user = $store.state?.user || {}
  const candidate = user.id || user.userId || user.uid
  if (candidate) {
    return candidate
  }
  const res = await $fetch.get("/system/auth/getCurrentUser")()
  return res.data?.id || res.data?.userId || res.data?.uid
}

const applyConfigDetailData = (detail) => {
  resetConfigForm()
  Object.keys(configForm).forEach(key => {
    if (typeof detail?.[key] !== "undefined") {
      configForm[key] = detail[key]
    }
  })
  configForm.singleTradeFee = detail?.singleTradeFee ?? 0.3
  configForm.failSingleTradeFee = detail?.failSingleTradeFee ?? detail?.singleTradeFee ?? 0.3
  ;(detail?.paymentSelectRules || []).forEach(item => normalizeRuleItem(item))
  configForm.paymentSelectRules = detail?.paymentSelectRules || []
  configForm.failTimesLimit = configForm.failTimesLimit || 3
  configForm.settleWeek = configForm.settleWeek ? String(configForm.settleWeek).split(",") : [...DEFAULT_SETTLE_WEEK]
}

const validatePassword = (rule, value, callback) => {
  if (!value) {
    callback()
    return
  }
  if (value.length > 12) {
    callback(new Error("密码最长12位"))
    return
  }
  if (!/[A-Z]/.test(value) || !/[a-z]/.test(value) || !/\d/.test(value) || !/[!@#$%^&*()_+\-=[\]{}|;:,.?]/.test(value)) {
    callback(new Error("密码需包含大写字母、小写字母、数字和常见符号"))
    return
  }
  callback()
}

const buildRandomPassword = () => {
  const requiredGroups = [
    "ABCDEFGHJKLMNPQRSTUVWXYZ",
    "abcdefghijkmnopqrstuvwxyz",
    "23456789",
    "!@#$%^&*()_+-=[]{}|;:,.?"
  ]
  const result = requiredGroups.map(group => group[Math.floor(Math.random() * group.length)])
  while (result.length < 12) {
    result.push(PASSWORD_CHARSET[Math.floor(Math.random() * PASSWORD_CHARSET.length)])
  }
  return result.sort(() => Math.random() - 0.5).join("")
}

const generatePassword = () => {
  basicForm.password = buildRandomPassword()
  basicFormRef.value?.validateField("password", () => {})
}

const checkuserName = (rule, value, callback) => {
  if (value === "") {
    callback("请输入商户名")
  } else if (value.length < 4) {
    callback(new Error("商户名至少4位"))
  } else {
    callback()
  }
}

const cardKindsOptions = (wayType) => {
  return wayType === "WALLET" ? data.walletList : data.cardTypeList
}

const handleWayTypeChange = (item) => {
  if (!item) return
  item.cardKinds = []
  item.cardKindsValue = ""
}

const handleDecimalInput = (value, field) => {
  const regex = /^(\d+)?(\.\d{0,4})?$/
  if (regex.test(value) || value === "") {
    configForm[field] = value
  }
}

const checkValue = (rule, value, callback, type, msg) => {
  if (!value) {
    callback()
    return
  }
  if (!Regulars[type].test(value)) {
    callback(new Error(msg || "请输入正确的内容"))
  } else {
    callback()
  }
}

const checkPaymentFactor = (rule, value, callback) => {
  if (value === "" || value === null || value === undefined) {
    callback(new Error("请输入支付系数"))
    return
  }
  if (!/^\d+(\.\d+)?$/.test(String(value))) {
    callback(new Error("支付系数必须为数字"))
    return
  }
  const num = Number(value)
  if (Number.isNaN(num) || num < 1 || num > 1.1) {
    callback(new Error("支付系数必须大于等于1且小于等于1.1"))
    return
  }
  callback()
}

const getFiles = (val, label) => {
  configForm[label] = val.filePath
}

const deleteConfigRule = (index) => {
  configForm.paymentSelectRules.splice(index, 1)
}

const saveTable = (item, index) => {
  item.countryCodes = (item.countryCodes || []).filter(v => !v.startsWith('continent_'))
  configRuleForm.value?.validateField([
    "paymentSelectRules." + index + ".countryCodes",
    "paymentSelectRules." + index + ".wayType",
    "paymentSelectRules." + index + ".cardKinds",
    "paymentSelectRules." + index + ".minAmount",
    "paymentSelectRules." + index + ".maxAmount",
    "paymentSelectRules." + index + ".limitedDailyAmount",
    "paymentSelectRules." + index + ".weight",
    "paymentSelectRules." + index + ".paymentAccountId",
  ], (valid) => {
    if (valid) {
      item.isEdit = false
      normalizeRuleItem(item)
    }
  })
}

const addItem = () => {
  const value = (configForm.paymentSelectRules || []).map(v => v.weight).reduce((pre, cur) => Number(pre) + Number(cur), 0)
  const weight = 100 - value > 0 ? 100 - value : 0
  configForm.paymentSelectRules.push({
    countryCodes: [],
    currencies: [],
    wayType: "CARD",
    cardKinds: [],
    minAmount: 0,
    maxAmount: 0,
    limitedDailyAmount: "",
    weight,
    paymentAccountId: "",
    paymentAccountName: "",
    isEdit: true,
  })
}

const validateBasicForm = async () => {
  try {
    await basicFormRef.value?.validate()
    return true
  } catch {
    return false
  }
}

const validateConfigForm = async () => {
  try {
    await configRuleForm.value?.validate()
    return true
  } catch {
    return false
  }
}

const buildBasicParams = () => {
  const params = {
    ...basicForm,
  }
  if (!params.password) {
    delete params.password
  } else {
    params.password = md5(params.password)
  }
  if (!params.id) {
    delete params.id
  }
  delete params.groupId
  return params
}

const buildConfigPayload = () => {
  const temp = JSON.parse(JSON.stringify(configForm))
  temp.settleWeek = Array.isArray(temp.settleWeek) ? temp.settleWeek.join(",") : temp.settleWeek
  return temp
}

const buildBasicSnapshot = () => JSON.stringify({
  id: basicForm.id || "",
  userName: basicForm.userName || "",
  remark: basicForm.remark || "",
  password: basicForm.password || "",
  roleId: basicForm.roleId || "",
  stopSettlement: basicForm.stopSettlement ?? 0,
})

const buildConfigSnapshot = () => JSON.stringify({
  config: isProfileMode.value ? {} : buildConfigPayload(),
  syncChildConfig: !!state.syncChildConfig,
})

const markFormSnapshot = () => {
  initialBasicSnapshot.value = buildBasicSnapshot()
  initialConfigSnapshot.value = buildConfigSnapshot()
}

const hasFormChanged = () => {
  if (!initialBasicSnapshot.value && !initialConfigSnapshot.value) {
    return false
  }
  return initialBasicSnapshot.value !== buildBasicSnapshot()
    || initialConfigSnapshot.value !== buildConfigSnapshot()
}

const saveBasicInfo = async () => {
  const params = buildBasicParams()
  if (isEdit.value) {
    await editUser(params)
    return basicForm.id
  }
  const userId = await addUser(params)
  basicForm.id = userId
  return userId
}

const saveUserConfig = async (userId) => {
  const config = buildConfigPayload()
  await $fetch.post("/system/user/configDetail/save")({
    id: userId,
    config,
    syncChildConfig: state.syncChildConfig,
  })
}

const handlePrimaryAction = async () => {
  if (state.initializing) {
    return
  }
  if (isProfileMode.value) {
    const valid = await validateBasicForm()
    if (!valid) return
    state.submitting = true
    try {
      await editUser(buildBasicParams())
      $tm_message_box.toast("编辑商户成功", { type: "success" })
      markFormSnapshot()
      props.ok()
    } finally {
      state.submitting = false
    }
    return
  }

  if (state.step === 1) {
    const valid = await validateBasicForm()
    if (!valid) return
    if (isCommonAdminCreateFlow.value) {
      state.initializing = true
      try {
        const ok = await loadCurrentUserConfigAsTemplate()
        if (!ok) {
          $tm_message_box.toast("获取当前管理员配置失败，请稍后重试", { type: "error" })
          return
        }
        state.step = 2
      } finally {
        state.initializing = false
      }
      return
    }
    state.step = 2
    return
  }

  const basicValid = await validateBasicForm()
  if (!basicValid) return
  if (!isCommonAdminCreateFlow.value) {
    const configValid = await validateConfigForm()
    if (!configValid) return
  }

  state.submitting = true
  try {
    const userId = await saveBasicInfo()
    if (!isCommonAdminCreateFlow.value) {
      await saveUserConfig(userId)
    }
    markFormSnapshot()
    $tm_message_box.toast(`${isEdit.value ? "编辑" : "新增"}商户成功`, { type: "success" })
    props.ok()
  } finally {
    state.submitting = false
  }
}

const handleModalClose = () => {
  handleBeforeClose(() => {
    props.close()
  })
}

const handleBeforeClose = (done) => {
  if (state.submitting || state.initializing) {
    return
  }
  if (!hasFormChanged()) {
    done()
    return
  }
  $tm_message_box.confirm("当前内容尚未提交，确认关闭吗？", "", { type: "warning" }).then(
    () => done(),
    () => {}
  )
}
</script>

<style lang="less" scoped>
.user-steps {
  margin-bottom: 20px;
}

.basic-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px;
  border-radius: 8px;
  background: @bg-table-header;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  .label {
    color: #606266;
    flex-shrink: 0;
  }
  .value {
    color: #303133;
    word-break: break-all;
  }
}

.basic-form {
  .el-form-item {
    .el-form-item__label {
      justify-content: flex-start;
      margin-bottom: 0;
    }
  }
}

.key-form {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 0 12px;
  min-height: 32px;
  line-height: 32px;
}

.flex-box {
  display: flex;
  gap: 10px;
}

.flex-box-fee {
  width: 100%;
  justify-content: space-between;
}

.flex-box-fee :deep(.el-form-item) {
  flex: 1;
  min-width: 0;
}

.flex-box-fee :deep(.el-form-item__content) {
  flex: 1;
  min-width: 0;
}

.flex-box-fee :deep(.el-form-item__content > span) {
  display: inline-block;
  min-width: 140px;
  padding-left: 24px;
  box-sizing: border-box;
}

.flex-box-settle,
.flex-box-ban {
  :deep(.el-form-item__label) {
    width: 170px !important;
  }
}

.flex-box-other {
  :deep(.el-form-item) {
    width: 350px;
  }
  :deep(.el-form-item__content) {
    align-items: flex-start;
  }
}

.sub-title {
  font-weight: 600;
  font-size: 18px;
  margin: 16px 0;
}

.fee-card {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
  width: 100%;
  box-sizing: border-box;
}

.fee-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.fee-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: @bg-table-header;
  .fee-label {
    color: #606266;
    font-size: 12px;
    line-height: 18px;
    flex-shrink: 0;
    min-width: 130px;
  }
  .fee-value {
    color: #303133;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    flex: 1;
    text-align: right;
    min-width: 150px;
    word-break: break-all;
  }
}

@media screen and (max-width: 1400px) {
  .fee-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 900px) {
  .fee-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

:deep(.el-table__empty-block) {
  .el-empty {
    display: none;
  }
  .el-table__empty-text {
    &::before {
      content: '暂无数据';
    }
  }
}

.img-box {
  position: relative;
  i {
    position: absolute;
    top: -4px;
    right: -4px;
    font-size: 20px;
    cursor: pointer;
  }
}

.table-ref {
  :deep(.el-form-item__label) {
    padding: 0;
  }
  :deep(.el-form-item) {
    margin-bottom: 0;
  }
}

.settle-week-select {
  :deep(.el-select__tags .el-tag) {
    flex-basis: 100%;
    max-width: 100%;
  }
}

.settle-week-form-item {
  width: 562px;
}
</style>
